import { EDITOR } from 'constants/actionTypes';
import request from 'utils/request_ajax';
import scrollIntoView from 'utils/scroll_into_view';
import dispatchEvent from 'utils/dispatch_event';
import { getElement } from 'selectors/editor';
import elementsActions from 'actions/editor/elements';

/* --------------- Action Creators --------------- */

const types = EDITOR.COMMENTARY;

const showCommentingContainer = () => ({
  type: types.SHOW,
});

const hideCommentingContainer = () => ({
  type: types.HIDE,
});

const requestThreads = id => ({
  type: types.REQUEST,
  id,
});

const requestThreadsSuccess = payload => ({
  type: types.REQUEST_SUCCESS,
  payload,
});

const requestThreadsFail = () => ({
  type: types.REQUEST_FAILURE,
});

const createThread = thread => ({
  type: types.CREATE_THREAD,
  thread,
});

const resolveThreadById = id => ({
  type: types.RESOLVE_THREAD,
  id,
});

const postReply = (comment, threadId) => ({
  type: types.POST_REPLY,
  threadId,
  comment,
});

export const setActiveThread = id => ({
  type: types.SET_ACTIVE_THREAD,
  id,
});

export const setUnactiveThread = id => ({
  type: types.SET_UNACTIVE_THREAD,
  id,
});

/* --------------- Actions --------------- */

/**
 * Fetch comments of a given Element
 * @param  {integer} id - Element id whose comments will be fetched
 */
export const fetchCommentary = id => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    dispatch(requestThreads(id));
    const { commentary_path } = getElement(getState().editor, id);
    request
      .get(commentary_path)
      .then(payload => {
        dispatch(requestThreadsSuccess(payload));
        resolve(payload);
      })
      .fail(error => {
        dispatch(requestThreadsFail());
        reject(error);
      });
  });

export const refetchCommentary = () => (dispatch, getState) => {
  const { elementId } = getState().editor.commentary;
  dispatch(fetchCommentary(elementId));
};

/**
 * Show commentary container
 * @param {integer} id - Element Id whose comments will be displayed
 */
export const toggleCommentary = id => (dispatch, getState) => {
  const { visible, elementId } = getState().editor.commentary;
  const sameElement = elementId === id;
  if (visible && sameElement) {
    dispatch(hideCommentingContainer());
  } else if (visible && !sameElement) {
    dispatch(fetchCommentary(id));
  } else {
    dispatch(showCommentingContainer());
    dispatch(fetchCommentary(id));
  }

  // @NOTE we need to fire the resize event in order to force Redactor to
  // recompute the fixed toolbar position after showing the commentary sidebar.
  dispatchEvent('resize');
};

export const autoFetchCommentary =
  (elementId, threadId = null, scrollOnLoad = false) =>
  (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const { visible } = getState().editor.commentary;
      if (!visible) dispatch(showCommentingContainer());
      dispatch(fetchCommentary(elementId)).then(() => {
        if (threadId) dispatch(setActiveThread(threadId));
        if (scrollOnLoad) {
          scrollIntoView(`#element-${elementId}`);
          scrollIntoView(
            `#thread-${threadId}`,
            '.Commentary .ScrollableContainer',
          );
        }
        resolve();
      });
    });

/**
 * Hide commentary container
 */
export const hideCommentary = () => (dispatch, getState) => {
  dispatch(hideCommentingContainer());
  dispatchEvent('resize');
};

/**
 * Resolving a single threads
 * @param  {integer|string} id - the id of the thread to resolve
 */
export const resolveThread = id => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const { updatePath } = getState().editor.commentary.threads[id];
    const { isTemplate } = getState().editor;
    request
      .put(updatePath, { status: 'resolved', isTemplate })
      .then(data => {
        dispatch(resolveThreadById(id));
        dispatch(elementsActions.updateCommentsCount());
        resolve(data);
      })
      .fail(reject);
  });

/**
 * Post a new comment on a thread or as a reply
 * @param  {object} comment
 * @prop  {string} comment.body
 * @param  {integer|string} [threadId=null]
 * @desc When threadId is present, a comment is considered a reply on a thread.
 * otherwise, the comment will create a new thread.
 */
export const postComment =
  ({ body }, threadId = null) =>
  (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const { addCommentPath } = getState().editor.commentary;
      if (threadId) {
        request
          .post(addCommentPath, { body, thread_id: threadId })
          .then(comment => {
            dispatch(postReply(comment, threadId));
            dispatch(elementsActions.updateCommentsCount());
            resolve(comment);
          })
          .fail(reject);
      } else {
        request
          .post(addCommentPath, { body, thread_id: threadId })
          .then(thread => {
            dispatch(createThread(thread));
            dispatch(elementsActions.updateCommentsCount());
            resolve(thread);
          })
          .fail(reject);
      }
    });
